/**
 * New Relic Browser agent initialization.
 *
 * Configured at build time via REACT_APP_NEWRELIC_* env vars sourced from
 * Terraform outputs (newrelic_browser_application_id +
 * newrelic_browser_license_key on the skills-marketplace module). The
 * license key is a Browser-scoped ingest key, intended to be shipped in
 * client JS — it only allows ingesting PageView / JavaScriptError / Ajax /
 * BrowserInteraction events into the matching application.
 *
 * Called once from index.tsx before React renders, so the agent is in
 * place to capture early errors and the initial PageView.
 */

import { BrowserAgent } from "@newrelic/browser-agent/loaders/browser-agent";
import { env } from "./env";

let agent: BrowserAgent | null = null;

/**
 * ServiceNow CI-ID for the web component (ai-marketplace-web). Stamped onto
 * every event the Browser agent emits — including distributed-tracing Span
 * events — under the attribute key `tags.coxauto_ci-id`.
 *
 * Why the odd `tags.`-prefixed key: the Reliability Central / PRR
 * "Distributed Tracing" control (rule 4) runs
 *   SELECT count(*) FROM Span WHERE tags.coxauto_ci-id = '<CI>'
 * For APM-instrumented services NR's backend stamps entity tags onto each
 * span as real `tags.*` attributes, so the query matches. The Browser agent
 * emits NO `tags.*` namespace on its spans and NR does not decorate Browser
 * spans with entity tags (verified empirically: a Browser span's keyset has
 * no `tags.*` keys), so tagging the entity in Terraform can never satisfy
 * this check. Setting the literal attribute key here is what puts
 * `tags.coxauto_ci-id` on the Browser spans so the control passes. The
 * matching entity tag (Terraform) still drives the entity-search controls
 * (Transaction Monitoring, SLO).
 */
const COXAUTO_CI_ID = "CI2413776";

export function initNewRelic(): void {
  if (agent) return;

  const accountID = env.REACT_APP_NEWRELIC_ACCOUNT_ID;
  const applicationID = env.REACT_APP_NEWRELIC_APPLICATION_ID;
  const licenseKey = env.REACT_APP_NEWRELIC_LICENSE_KEY;
  const trustKey = env.REACT_APP_NEWRELIC_TRUST_KEY || accountID;

  if (!accountID || !applicationID || !licenseKey) {
    // Missing config — local dev or a deploy that hasn't seeded the NR
    // vars yet. Skip silently rather than throwing; the SPA must keep
    // rendering even without observability.
    return;
  }

  try {
    agent = new BrowserAgent({
      info: {
        beacon: "bam.nr-data.net",
        errorBeacon: "bam.nr-data.net",
        licenseKey,
        applicationID,
        sa: 1,
      },
      init: {
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
      },
      loader_config: {
        accountID,
        trustKey,
        agentID: applicationID,
        licenseKey,
        applicationID,
      },
    });

    // Stamp the CI-ID onto every event the agent reports (PageView, Ajax,
    // BrowserInteraction, JavaScriptError, and distributed-tracing Span).
    // persist=true so it survives route changes within the SPA session.
    // This is what satisfies the Distributed Tracing PRR control for a
    // Browser app — see COXAUTO_CI_ID above.
    agent.setCustomAttribute("tags.coxauto_ci-id", COXAUTO_CI_ID, true);
  } catch (err) {
    // CSP violations, browser extension interference, or unexpected
    // agent bugs must not block the SPA from rendering. Leave agent
    // null so reportError() no-ops, but surface the failure so a quiet
    // outage is at least visible in DevTools.
    console.error("[NewRelic] BrowserAgent init failed — error reporting disabled:", err);
  }
}

/**
 * Associate the current session with an authenticated user so New Relic can
 * report Unique Users. Populates `enduser.id` on all subsequent PageView,
 * Ajax, and BrowserInteraction events.
 *
 * Pass the opaque MSAL homeAccountId — not an email — to keep PII off a
 * third-party SaaS. Unique-count queries work identically with opaque IDs:
 *   SELECT uniqueCount(enduser.id) FROM PageView WHERE appName = '...'
 *
 * Silently no-ops when the agent isn't initialized (local dev, missing
 * env vars), so callers don't need to gate on initialization state.
 */
export function setUser(userId: string): void {
  if (!agent) return;
  try {
    agent.setUserId(userId);
  } catch (err) {
    console.error("[NewRelic] setUserId failed:", err);
  }
}

/**
 * Forward a caught error to New Relic so it counts toward the
 * JavaScriptError alert. Without this, .catch(console.error) handlers
 * stay invisible to the agent — only uncaught errors auto-instrument.
 *
 * `context` tags the error with the call site so on-call can route
 * cheaply: "catalog-fetch", "auth-init", "skill-files-fetch", etc.
 *
 * Silently no-ops when the agent isn't initialized (local dev, missing
 * env vars), so callers don't need to gate on initialization state.
 */
export function reportError(
  error: unknown,
  context?: string,
  customAttributes: Record<string, string | number | boolean> = {},
): void {
  if (!agent) return;

  const err = error instanceof Error ? error : new Error(String(error));
  const attrs = context ? { ...customAttributes, context } : customAttributes;

  try {
    agent.noticeError(err, attrs);
  } catch (noticeErr) {
    // Defense-in-depth: noticeError throwing must not break the caller's
    // catch block. Log both errors so a silent reporting outage is at
    // least visible in DevTools.
    console.error("[NewRelic] noticeError failed:", err, noticeErr);
  }
}
